import React from 'react';
import { ITableDataSource } from 'models/table';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { useTranslations } from 'hooks/useTranslations';
import { EIconTypes } from 'constants/Icons';
import Icon from 'components/atoms/Icon/Icon';
import { formatDate, leftDays } from 'utils/date';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import { getColumnTitleFromCustomField } from 'utils/table';
import { ICustomFieldByObjectType, ECustomFieldType } from 'models/customField';
import { LETypes } from 'constants/LegalEntityTypes';
import { EPermission } from 'models/permissions';
import { ELicenceStatus } from 'pages/LegalEntitiesPage/helpers';
import { applicationStatusChips } from './helpers';
import { StyledLicenceInfo } from './styles';

interface ILicenceInfo {
  data: ITableDataSource;
  leftDaysNumber: number;
  willExpireLimitInfo: number;
  t: any;
  isActive: boolean;
}
const LicenceInfo = ({
  data,
  leftDaysNumber,
  willExpireLimitInfo,
  t,
  isActive,
}: ILicenceInfo) => {
  const infoText = [];
  if (data.hasInactiveOwner)
    infoText.push(t('legalEntity.licenceDetails.hasInactiveOwner.text'));

  if (isActive) {
    if (data.hasAnyFirearmIllegal)
      infoText.push(t('legalEntity.licenceDetails.hasAnyIllegalFirearm.text'));

    if (data.hasAnyFirearmInactive)
      infoText.push(t('legalEntity.licenceDetails.hasAnyInactiveFirearm.text'));

    if (data.hasAnyLeDataChanged)
      infoText.push(t('legalEntity.licenceDetails.hasAnyLeDataChanged.text'));

    if (data.hasAnyFirearmNotAssignedToLegal)
      infoText.push(t('legalEntity.licenceDetails.hasAnyFirearmNotAssignedToLegal.text'));

    if (data.expiryDate && leftDaysNumber <= willExpireLimitInfo && !data.renewed)
      infoText.push(
        leftDaysNumber > 0
          ? `${t('legalEntity.licenceDetails.willExpire.text')} ${leftDaysNumber} ${t(
              'legalEntity.licenceDetails.willExpire.days',
            )}`
          : t('legalEntity.licenceDetails.willExpireToday.text'),
      );
  }

  if (infoText.length === 1) {
    return <span style={{ marginLeft: '10px' }}>{infoText[0]}</span>;
  }
  return (
    <>
      {infoText.map((label) => (
        <div>
          <li style={{ marginLeft: '10px' }}>{label}</li>
        </div>
      ))}
    </>
  );
};

interface ILicenceTableCard {
  data: ITableDataSource;
  navigateToDetail: (arg: string, name: string) => void;
  shortDateFormat: string;
  willExpireLimitInfo: number;
  customFields?: ICustomFieldByObjectType[];
  selectedLanguage?: string;
}

export const LicenceTableCard = ({
  data,
  shortDateFormat,
  navigateToDetail,
  willExpireLimitInfo,
  customFields = [],
  selectedLanguage,
}: ILicenceTableCard) => {
  const { t } = useTranslations();
  const leftDaysNumber = Number(leftDays(data.expiryDate, t, true));

  const showInfo =
    data.hasAnyFirearmIllegal ||
    data.hasAnyFirearmInactive ||
    data.hasAnyLeDataChanged ||
    data.hasAnyFirearmNotAssignedToLegal ||
    (data.expiryDate &&
      leftDaysNumber &&
      leftDaysNumber <= willExpireLimitInfo &&
      !data.renewed);

  return (
    <Grid
      container
      sx={{
        border: `1px solid ${colors.gray200}`,
        borderRadius: '4px',
        overflow: 'hidden',
        mt: 1,
        backgroundColor: colors.gray100,
      }}
    >
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licences.licenceName.label')}
        </Typography>
        <Typography variant="body2">{data.weaponLicenceName}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licences.weaponLicenceNumber.label')}
        </Typography>
        <Typography variant="body2">{data.weaponLicenceNumber}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licences.legalName.label')}
        </Typography>
        <Typography variant="body2">{data.legalName}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licences.legalType.label')}
        </Typography>
        <Chips
          text={data.legalType}
          fullWidth
          variant={
            data.legalType === LETypes.individual
              ? EChipsVariants.primary
              : EChipsVariants.inactive
          }
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licences.licencingAuthority.label')}
        </Typography>
        <Typography variant="body2">{data.licencingAuthority}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licences.expirationDate.label')}
        </Typography>
        <Typography variant="body2">
          {data.expiryDate ? formatDate(data.expiryDate, shortDateFormat) : '-'}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licences.status.label')}
        </Typography>
        {applicationStatusChips(data.applicationStatus, t, true)}
      </Grid>
      {customFields
        .filter((customField) => customField.visibleInSearchResults)
        .map((customField) => {
          let value: string = '-';
          if (data?.customFields?.length) {
            const cf = data.customFields.find(
              (cField: any) => cField.customFieldName === customField.name,
            );
            if (cf) {
              if (cf.customFieldType === ECustomFieldType.BOOL && t) {
                if (cf.value === null) {
                  value = '-';
                } else {
                  value = cf.value
                    ? t('customFields.value.yes.label')
                    : t('customFields.value.no.label');
                }
              } else if (cf.customFieldType === ECustomFieldType.DATE) {
                value = formatDate(cf.value, shortDateFormat);
              } else {
                value = cf.value || '-';
              }
            }
          }
          return (
            <Grid size={{ xs: 12, sm: 6 }} sx={{ px: 2, pt: 2 }} key={customField.uniqueId}>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {getColumnTitleFromCustomField(customField, selectedLanguage)}
              </Typography>
              <Typography variant="body2">{value}</Typography>
            </Grid>
          );
        })}
      {((showInfo && data.applicationStatus === ELicenceStatus.ACTIVE) ||
        data.hasInactiveOwner) && (
        <Grid size={{ xs: 12 }} sx={{ px: 2, pt: 2 }}>
          <Grid sx={{ alignItems: 'flex-start', gap: 1, display: 'flex' }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('licences.info.label')}
            </Typography>
            <Icon type={EIconTypes.infoWarning} fontSize={20} />
          </Grid>
          <StyledLicenceInfo size={{ xs: 12 }}>
            <LicenceInfo
              data={data}
              leftDaysNumber={leftDaysNumber}
              willExpireLimitInfo={willExpireLimitInfo}
              t={t}
              isActive={data.applicationStatus === ELicenceStatus.ACTIVE}
            />
          </StyledLicenceInfo>
        </Grid>
      )}

      <Grid
        container
        size={{ xs: 12 }}
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: {
            xs: `1px solid ${colors.primary100}`,
            lg: 'none',
          },
          px: 2,
          pb: 2,
          mt: 1,
          pt: 2,
        }}
      >
        <Grid size={{ xs: 12, sm: 6 }} sx={{ mb: { xs: 2, sm: 0 } }}>
          <Typography variant="button" sx={{ textTransform: 'uppercase' }}>
            {t('licences.actions.label')}:{' '}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant={EButtonVariants.outlined}
            label={t('licences.view.label')}
            onClick={() => navigateToDetail(data.id as string, data.typeToLink)}
            fullWidth
            size={EButtonSizes.small}
            permission={[
              EPermission.VIEW_GLOBAL_LICENCES,
              EPermission.VIEW_RESTRICTED_LICENCES,
              EPermission.VIEW_LICENCES_LE,
            ]}
            permissionOr
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
